/**
 * One announcement as a person sees it (Oct 2026): their blocks (image + markdown-lite text, placeholders already
 * filled server-side) and the optional link. Used by the Home card, the pop-up and the admin preview, so all
 * three look the same. Also the shared, cached "my announcements" loader.
 */
import { useEffect, useState } from 'react';
import type { AnnouncementsForMeResponse } from '@daaily/shared';
import { renderMarkdown } from '../review/markdown';
import { callAnnouncementsForMe } from './callables';

export type MyAnnouncement = AnnouncementsForMeResponse['announcements'][number];
export interface RenderedView { title: string; blocks: { id: string; body: string; imageUrl: string | null }[] }

export function AnnouncementBody({ rendered, linkUrl, linkLabel }: { rendered: RenderedView; linkUrl?: string | null; linkLabel?: string | null }) {
  return (
    <div className="space-y-3">
      {rendered.blocks.map((b) => (
        <div key={b.id} className="space-y-2">
          {b.imageUrl && <img src={b.imageUrl} alt="" className="block max-w-full h-auto rounded-md" loading="lazy" />}
          {b.body.trim() && <div className="md-body text-sm text-fg" dangerouslySetInnerHTML={{ __html: renderMarkdown(b.body) }} />}
        </div>
      ))}
      {linkUrl && (
        <a href={linkUrl} target="_blank" rel="noreferrer" className="inline-block text-sm text-link hover:underline">{linkLabel?.trim() || 'Open link'}</a>
      )}
    </div>
  );
}

// ── Cached loader shared by the Home card and the pop-up (one call per 5 minutes) ─────────────────
let cache: { at: number; list: MyAnnouncement[] } | null = null;
let inflight: Promise<MyAnnouncement[]> | null = null;
const TTL = 5 * 60_000;
export const invalidateAnnouncements = () => { cache = null; };

export function useMyAnnouncements(): MyAnnouncement[] | null {
  const fresh = cache && Date.now() - cache.at < TTL ? cache.list : null;
  const [list, setList] = useState<MyAnnouncement[] | null>(fresh);
  useEffect(() => {
    if (list !== null) return;
    let alive = true;
    inflight ??= callAnnouncementsForMe().then((r) => r.data.announcements).finally(() => { inflight = null; });
    inflight
      .then((l) => { cache = { at: Date.now(), list: l }; if (alive) setList(l); })
      .catch(() => { if (alive) setList([]); });
    return () => { alive = false; };
  }, [list]);
  return list;
}
